<template>
  <div></div>
</template>

<script>
import { mapGetters } from 'vuex'
import EventBus from '@/eventBus'
import VueToastedUtils from '@/utils/vue-toasted.utils.js'
import utils from '@/util'
import { IMPORT_OPTIONS } from '@/constant/eventActions/ImportEventActions.js'

export default {
  data() {
    return {
      currentToast: null,
      title: 'Importing slides…',
    }
  },

  computed: {
    ...mapGetters('aiChatbotImportPptxPdf', [
      'isCurrentPresentationImportingSlides',
      'selectedImportOption',
      'importProgress',
      'canUseImportPipelineV2',
    ]),

    shouldShowToaster() {
      const isImportOnly = this.selectedImportOption === IMPORT_OPTIONS.IMPORT_ONLY
      return this.isCurrentPresentationImportingSlides && isImportOnly
    },
    importErrorText() {
      return `${this.$i18nPresenter.t('Import failed')}.`
    },

    // Live status for a v2 import. Null means "say nothing new" — the toaster
    // keeps its generic title, which is what v1 and the cloud-reconcile path
    // get since neither reports progress.
    //
    // Gated on the same flag as the pipeline itself, so with it off this
    // component behaves exactly as it did before.
    progressText() {
      if (!this.canUseImportPipelineV2) return null
      const progress = this.importProgress
      if (!progress) return null

      const slides = progress.slides || {}
      const captions = progress.captions || {}

      if (progress.phase === 'converting') {
        return this.$i18nPresenter.t('Preparing your file...')
      }
      if (progress.phase === 'importing') {
        if (!slides.total) return this.$i18nPresenter.t('Importing slides...')
        return this.$i18nPresenter.t('Importing slide {done} of {total}', {
          done: slides.created || 0,
          total: slides.total,
        })
      }
      // The slides already exist and are editable by now — this phase is only
      // filling in descriptions, so the copy should not read as "still importing".
      if (progress.phase === 'captioning') {
        if (!captions.total) return this.$i18nPresenter.t('Adding descriptions...')
        return this.$i18nPresenter.t('Adding descriptions ({done} of {total})', {
          done: captions.done || 0,
          total: captions.total,
        })
      }
      return null
    },
  },

  watch: {
    shouldShowToaster(newValue) {
      if (!newValue) return

      this.showImportingToaster()
    },

    progressText(text) {
      this.updateToasterText(text)
    },
  },

  mounted() {
    EventBus.$on('import: hide_is_importing_toaster', () => {
      this.hideCurrentToaster()
    })
    EventBus.$on('import: show_import_status_success', () => {
      this.showSuccessToaster()
    })
    EventBus.$on('import: show_import_status_error', () => {
      this.showErrorToaster()
    })

    if (this.shouldShowToaster) {
      this.showImportingToaster()
    }
  },

  destroyed() {
    EventBus.$off('import: hide_is_importing_toaster')
    EventBus.$off('import: show_import_status_success')
    EventBus.$off('import: show_import_status_error')
  },

  methods: {
    showImportingToaster() {
      this.currentToast = VueToastedUtils.showToast(
        this.progressText || this.$i18nPresenter.t(this.title),
        {},
        {
          className: 'custom-toast__loading',
          hasReturnValue: true,
          action: {
            text: this.$i18nPresenter.t('Dismiss'),
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            },
          },
        },
      )
    },

    // Rewrites just the message, leaving the icon and the Dismiss button alone.
    //
    // NOT toastObject.text(): vue-toasted implements that as
    // `el.innerHTML = text` (vue-toasted.js:719), which would wipe the action
    // button VueToastedUtils attached and any icon span it prepended. Updating
    // the text node in place is the only way to change the copy without
    // rebuilding the toast — and rebuilding it would flicker on every tick.
    updateToasterText(text) {
      if (!text) return
      const el = this.currentToast?.el
      if (!el) return
      const textNode = Array.from(el.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE,
      )
      if (textNode) {
        textNode.nodeValue = ` ${text}`
      }
    },

    hideCurrentToaster() {
      if (this.currentToast) {
        this.$toasted.clear()
      }
    },

    async showSuccessToaster() {
      this.hideCurrentToaster()
      await utils.sleep(500)
      this.currentToast = VueToastedUtils.showToast(this.$i18nPresenter.t('Your slides are ready!'), {}, {
        className: 'custom-toast__success',
        hasReturnValue: true,
        duration: 3000,
      })
    },

    async showErrorToaster() {
      this.hideCurrentToaster()
      await utils.sleep(500)
      this.currentToast = VueToastedUtils.showToast(this.importErrorText, {}, {
        className: 'custom-toast__error',
        hasReturnValue: true,
        duration: 3000,
      })
    },
  },
}
</script>
